import React, { useState, useEffect } from 'react';

const WhatsAppButton = () => {
  const [visible, setVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [number, setNumber] = useState('');
  const [message, setMessage] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [customMsg, setCustomMsg] = useState('');
  // FAB coexistence (Apr25 v2): when Aisha chat is open we hide WhatsApp
  // to avoid the two buttons stacking on top of each other on mobile.
  // AIStyleChatbot dispatches aisha:open / aisha:close on window.
  const [aishaOpen, setAishaOpen] = useState(false);

  useEffect(() => {
    const onOpen  = () => setAishaOpen(true);
    const onClose = () => setAishaOpen(false);
    window.addEventListener('aisha:open',  onOpen);
    window.addEventListener('aisha:close', onClose);
    return () => {
      window.removeEventListener('aisha:open',  onOpen);
      window.removeEventListener('aisha:close', onClose);
    };
  }, []);

  useEffect(() => {
    // Try fetching WhatsApp config from API first, fallback to localStorage
    const loadConfig = async () => {
      try {
        const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';
        const res = await fetch(`${API}/api/settings/whatsapp`);
        if (res.ok) {
          const data = await res.json();
          if (data.number) { setNumber(data.number); localStorage.setItem('whatsapp_config', JSON.stringify(data)); }
          if (data.defaultMessage) setMessage(data.defaultMessage);
          if (data.enabled === false) return; // disabled by admin
        }
      } catch {
        // Fallback to localStorage
        const cfg = JSON.parse(localStorage.getItem('whatsapp_config') || '{}');
        if (cfg.number) setNumber(cfg.number);
        if (cfg.defaultMessage) setMessage(cfg.defaultMessage);
      }
    };
    loadConfig();
    
    // Show button after 2 seconds
    const t = setTimeout(() => setVisible(true), 2000);
    
    // Show tooltip hint after 4 seconds
    const t2 = setTimeout(() => {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 4000);
    }, 4000);
    
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, []);

  const openWhatsApp = (msg) => {
    const text = encodeURIComponent(msg || message);
    window.open(`https://wa.me/${number}?text=${text}`, '_blank');
    setShowChat(false);
  };

  const QUICK_MSGS = [
    '💎 I want to know about your latest jewelry collection',
    '🛍️ I need help with my order',
    '💸 What are your wholesale/bulk pricing?',
    '🔄 I want to return/exchange a product',
    '📦 Where is my order?',
  ];

  if (!visible) return null;
  // Hide entirely if Aisha chat is open — avoids FAB overlap on mobile
  if (aishaOpen) return null;

  return (
    <>
      {/* Chat popup */}
      {showChat && (
        <div style={S.chatBox}>
          <div style={S.chatHeader}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={S.avatar}>💬</div>
              <div>
                <div style={S.agentName}>Glitz & Glam Support</div>
                <div style={S.agentStatus}>● Online now</div>
              </div>
            </div>
            <button style={S.closeChat} onClick={()=>setShowChat(false)}>✕</button>
          </div>
          
          <div style={S.chatBody}>
            <div style={S.bubble}>
              <p style={{ margin:0, fontSize:14, lineHeight:1.5 }}>
                👋 Namaste! How can we help you today?<br/>
                Choose a quick option or type your message below.
              </p>
            </div>
            
            <div style={S.quickWrap}>
              {QUICK_MSGS.map((m,i)=>(
                <button key={i} style={S.quickBtn} onClick={()=>openWhatsApp(m)}>{m}</button>
              ))}
            </div>
          </div>
          
          <div style={S.chatFooter}>
            <input
              style={S.chatInput}
              value={customMsg}
              onChange={e=>setCustomMsg(e.target.value)}
              placeholder="Type your message..."
              onKeyDown={e=>e.key==='Enter'&&openWhatsApp(customMsg)}
            />
            <button style={S.sendBtn} onClick={()=>openWhatsApp(customMsg)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {showTooltip && !showChat && (
        <div style={S.tooltip}>💬 Chat with us on WhatsApp!</div>
      )}

      {/* Main button */}
      <button
        style={{ ...S.btn, ...(showChat ? S.btnActive : {}) }}
        onClick={()=>setShowChat(!showChat)}
        aria-label="Chat on WhatsApp"
        title="Chat on WhatsApp"
      >
        {showChat ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="28" height="28" viewBox="0 0 32 32" fill="white">
            <path d="M16 2C8.268 2 2 8.268 2 16c0 2.516.657 4.876 1.809 6.921L2 30l7.271-1.906A13.93 13.93 0 0016 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 2c6.627 0 12 5.373 12 12S22.627 28 16 28c-2.179 0-4.221-.583-5.977-1.604l-.428-.254-4.316 1.133 1.062-4.196-.278-.452A11.94 11.94 0 014 16C4 9.373 9.373 4 16 4zm-3.34 6.064c-.222 0-.58.083-.887.414-.306.331-1.168 1.14-1.168 2.78s1.196 3.226 1.363 3.447c.167.22 2.328 3.709 5.726 5.054 3.398 1.344 3.398.896 4.011.84.613-.056 1.974-.806 2.252-1.585.278-.78.278-1.447.194-1.585-.083-.14-.305-.222-.64-.388-.334-.167-1.974-.975-2.28-1.086-.306-.111-.527-.167-.749.167-.222.333-.859 1.086-1.051 1.307-.194.222-.39.25-.725.083-.333-.167-1.407-.519-2.679-1.654-.990-.884-1.658-1.975-1.852-2.308-.194-.333-.021-.514.145-.68.15-.15.333-.388.5-.583.167-.194.222-.333.333-.556.111-.222.056-.417-.028-.583-.083-.167-.738-1.81-1.027-2.476-.267-.64-.543-.557-.749-.557z"/>
          </svg>
        )}
        {/* Pulse animation */}
        {!showChat && <span style={S.pulse}/>}
      </button>
    </>
  );
};

const S = {
  btn: {
    position: 'fixed', bottom: 24, right: 24, width: 56, height: 56,
    background: 'linear-gradient(135deg, #25D366, #128C7E)',
    border: 'none', borderRadius: '50%', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(37,211,102,0.4)',
    zIndex: 9999, transition: 'all 0.3s ease',
  },
  btnActive: {
    background: 'linear-gradient(135deg, #555, #333)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  },
  pulse: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    borderRadius: '50%', border: '3px solid rgba(37,211,102,0.6)',
    animation: 'whatsapp-pulse 2s infinite',
  },
  tooltip: {
    position: 'fixed', bottom: 24, right: 92,
    background: '#1a1a2e', color: '#e8e8f5',
    padding: '8px 14px', borderRadius: 8, fontSize: 13,
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
    zIndex: 9998, whiteSpace: 'nowrap',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  chatBox: {
    position: 'fixed',
    bottom: 96,
    // Centered horizontally, fills width with 14px edge gutter, capped to 380 on wide screens
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100vw - 28px)',
    maxWidth: 380,
    background: '#0e0b18', borderRadius: 20,
    boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
    border: '1px solid rgba(255,255,255,0.08)',
    zIndex: 9998, overflow: 'hidden',
    animation: 'slideUp 0.3s ease',
    // Clamp so chat doesn't spill off top of small screens
    maxHeight: 'calc(100vh - 140px)',
    display: 'flex',
    flexDirection: 'column',
  },
  chatHeader: {
    background: 'linear-gradient(135deg, #25D366, #128C7E)',
    padding: '14px 16px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
  },
  avatar: {
    width: 40, height: 40, background: 'rgba(255,255,255,0.2)',
    borderRadius: '50%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 20,
  },
  agentName: { color: 'white', fontWeight: 700, fontSize: 14 },
  agentStatus: { color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 2 },
  closeChat: { background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18, padding: 4 },
  chatBody: { padding: 16, flex: 1, overflowY: 'auto', minHeight: 200 },
  bubble: {
    background: 'rgba(255,255,255,0.06)', borderRadius: '12px 12px 12px 2px',
    padding: '12px 14px', color: '#e8e8f5', marginBottom: 14,
  },
  quickWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  quickBtn: {
    padding: '9px 12px', background: 'rgba(37,211,102,0.08)',
    border: '1px solid rgba(37,211,102,0.2)', borderRadius: 10,
    color: '#88d4a0', cursor: 'pointer', textAlign: 'left', fontSize: 12,
    transition: 'all 0.2s', lineHeight: 1.4,
  },
  chatFooter: {
    padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', gap: 8, alignItems: 'center',
  },
  chatInput: {
    flex: 1, padding: '9px 13px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
    color: '#e8e8f5', fontSize: 13, outline: 'none',
  },
  sendBtn: {
    width: 38, height: 38, background: 'linear-gradient(135deg,#25D366,#128C7E)',
    border: 'none', borderRadius: 12, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
    flexShrink: 0,
  },
};

// Inject animations
const waStyle = document.createElement('style');
waStyle.textContent = `
  @keyframes whatsapp-pulse {
    0%   { transform: scale(1);   opacity: 0.8; }
    70%  { transform: scale(1.6); opacity: 0; }
    100% { transform: scale(1.6); opacity: 0; }
  }
  @keyframes slideUp {
    from { transform: translateY(10px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
`;
document.head.appendChild(waStyle);

export default WhatsAppButton;
